import { IStorefrontSettings } from '../../../models/Storefront';
import { parseHeaderConfig, serializeConfig } from './headerFooterConfig';
import { parseHomePageConfig, serializeHomePageConfig } from './homePageConfig';
import { ISearchElementStyle, parseSearchPageConfig, serializeSearchPageConfig } from './searchPageConfig';

// Per-search-bar autocomplete configuration.
//
// The storefront has ONE master switch (StorefrontSettings.enableSearchAutocomplete) which decides
// whether suggestions are built for it at all. This block decides, per search bar, whether that bar
// shows a popout, which columns it shows, and how the popout looks — so a merchant can run rich
// suggestions in the header and a bare keyword list on the search page, or none at all on one of them.
//
// It lives inside the existing per-context config blobs (headerConfig / searchPageConfig /
// homePageConfig) rather than as columns on the storefront, because that is where every other piece of
// per-context presentation config already lives, and it keeps a bar's behaviour and its styling together.

export interface IAutocompleteColumns {
    /** Keyword phrases, category paths and part numbers — the first column. */
    keywords?: boolean;
    manufacturers?: boolean;
    tags?: boolean;
    /** Product mini-cards with thumbnails. The only column that costs an extra API call. */
    products?: boolean;
}

export interface IAutocompleteConfig {
    /** Whether THIS search bar shows a popout. Off unless explicitly true. */
    enabled?: boolean;
    /** Undefined means show — a bar switched on without touching columns gets the full popout. */
    columns?: IAutocompleteColumns;

    // --- Style parts. Each maps to one element of the popout; all use the shared element-style shape,
    // whose index signature also lets any other CSS property through.
    /** The flyout surface itself: background, border, radius, shadow. */
    panel?: ISearchElementStyle;
    /** A column heading ("Manufacturers", "Tags"…). */
    heading?: ISearchElementStyle;
    /** One suggestion row in its resting state. */
    item?: ISearchElementStyle;
    /**
     * The highlighted row — hovered, or reached with the arrow keys.
     *
     * Separate from `item` because the highlight cannot be an inline style: it is applied through a CSS
     * custom property. Without it a dark-themed storefront gets the default translucent-black highlight,
     * which is invisible on a dark panel.
     */
    itemActive?: ISearchElementStyle;
    /** A product mini-card in the products column. */
    product?: ISearchElementStyle;
    /** The product thumbnail image. */
    thumbnail?: ISearchElementStyle;
    /** The small result-count badge next to a suggestion. */
    count?: ISearchElementStyle;
}

/** The style parts an editor exposes, in the order they should be listed. */
export const AUTOCOMPLETE_STYLE_PARTS = [
    'panel', 'heading', 'item', 'itemActive', 'product', 'thumbnail', 'count',
] as const;

export type AutocompleteStylePart = typeof AUTOCOMPLETE_STYLE_PARTS[number];

export const AUTOCOMPLETE_PART_LABELS: Record<AutocompleteStylePart, string> = {
    panel: 'Panel',
    heading: 'Column Heading',
    item: 'Suggestion',
    itemActive: 'Suggestion (highlighted)',
    product: 'Product Card',
    thumbnail: 'Thumbnail',
    count: 'Count Badge',
};

export const AUTOCOMPLETE_PART_TOOLTIPS: Record<AutocompleteStylePart, string> = {
    panel: 'The flyout surface that drops below the search bar.',
    heading: 'The heading above each column.',
    item: 'One suggestion row at rest.',
    itemActive: 'A suggestion row while hovered or selected with the arrow keys.',
    product: 'One product mini-card in the products column.',
    thumbnail: 'The product image inside a mini-card.',
    count: 'The small "how many products" badge beside a suggestion.',
};

/** Every column on. What a bar gets when the merchant has not narrowed it. */
export const ALL_AUTOCOMPLETE_COLUMNS: Required<IAutocompleteColumns> = {
    keywords: true, manufacturers: true, tags: true, products: true,
};

/**
 * Whether a column is shown. Undefined means "not configured", which reads as ON — only an explicit
 * false hides a column. Mirrored exactly in ac-storefront-web.
 */
export function autocompleteColumnEnabled(columns: IAutocompleteColumns | undefined, key: keyof IAutocompleteColumns): boolean {
    return columns?.[key] !== false;
}

/**
 * Which search bar a config belongs to. Each keeps its settings in its own page-config blob, so the
 * header can run a rich popout while the search page runs a bare keyword list — or none at all.
 */
export type SearchBarContext = 'header' | 'homePage' | 'searchPage';

/** This bar's autocomplete block, or an empty config when it has never been touched. */
export function readAutocompleteConfig(settings: IStorefrontSettings, context: SearchBarContext): IAutocompleteConfig {
    switch (context) {
        case 'header': return parseHeaderConfig(settings.headerConfig).searchAutocomplete ?? {};
        case 'homePage': return parseHomePageConfig(settings.homePageConfig).searchAutocomplete ?? {};
        case 'searchPage': return parseSearchPageConfig(settings.searchPageConfig).searchAutocomplete ?? {};
    }
}

/**
 * Returns settings with this bar's autocomplete block replaced, leaving the rest of that bar's page
 * config untouched. Shared by the search-bar callout and the style panel so the two cannot drift on
 * which blob a context writes to.
 */
export function writeAutocompleteConfig(
    settings: IStorefrontSettings, context: SearchBarContext, next: IAutocompleteConfig,
): IStorefrontSettings {
    switch (context) {
        case 'header':
            return {
                ...settings,
                headerConfig: serializeConfig({ ...parseHeaderConfig(settings.headerConfig), searchAutocomplete: next }),
            };
        case 'homePage':
            return {
                ...settings,
                homePageConfig: serializeHomePageConfig({ ...parseHomePageConfig(settings.homePageConfig), searchAutocomplete: next }),
            };
        case 'searchPage':
            return {
                ...settings,
                searchPageConfig: serializeSearchPageConfig({ ...parseSearchPageConfig(settings.searchPageConfig), searchAutocomplete: next }),
            };
    }
}
